import { EntityService } from 'src/service/EntityService';

export type HsmSignRequest = {
    expectedDocumentVersion: string;
    hsmUsername: string;
    hsmPassword: string;
    keyId: string;
};

export type HsmKeyOption = {
    keyId: string;
    status?: string;
    algorithm?: string;
    createdAt?: string;
    updatedAt?: string;
};

export type HsmKeysRequest = {
    expectedDocumentVersion: string;
    hsmUsername: string;
    hsmPassword: string;
};

export type HsmKeysResponse = {
    success?: string;
    keys: HsmKeyOption[];
};

export type HsmSignResponse = {
    success?: string;
    agreementId: string;
    agreementStatusId: string;
    partyStatusId: string;
    signedAt?: string;
};

export type HsmSignError = {
    code: string;
    message: string;
    referenceId?: string;
    retryable: boolean;
};

export class AgreementService extends EntityService {

    static entity = 'iam-agreement';

    private static getHeaderValue(headers: any, name: string): string | undefined {
        const value = headers?.[name] || headers?.[name.toLowerCase()];
        return typeof value === 'string' ? value : undefined;
    }

    static update(id: string, data: any): Promise<any> {
        return this.axios.post(`${this.entity}/${id}/update`, data).then(res => res.data);
    }

    static create(data: any): Promise<any> {
        return this.axios.post(`${this.entity}/create`, data).then(res => res.data);
    }

    static uploadPDFFile(agreementId: string, data: any): Promise<any> {
        return this.axios.post(`${this.entity}/${agreementId}/pdf`, data).then(res => res.data);
    }

    static uploadDraft(data: any): Promise<any> {
        return this.axios.post(`${this.entity}/upload-draft`, data).then(res => res.data);
    }

    static sentEmailNotifyBuyer(agreementId: string, data: any): Promise<any> {
        return this.axios.put(`${this.entity}/${agreementId}/sent-email-notify-buyer`, data).then(res => res.data);
    }

    static publishSigning(agreementId: string, data: any = {}): Promise<any> {
        return this.axios.put(`${this.entity}/${agreementId}/publish-signing`, data).then(res => res.data);
    }

    static signHsm(agreementId: string, data: HsmSignRequest): Promise<HsmSignResponse> {
        return this.axios.put(`${this.entity}/${agreementId}/hsm-sign`, data).then(res => res.data);
    }

    static listHsmKeys(agreementId: string, data: HsmKeysRequest): Promise<HsmKeysResponse> {
        return this.axios.post(`${this.entity}/${agreementId}/hsm-keys`, data).then(res => res.data);
    }

    static getHsmSignError(error: any): HsmSignError {
        const data = error?.response?.data;
        const status = Number(error?.response?.status || 0);
        const code = typeof data?.code === 'string'
            ? data.code
            : (status === 401 ? 'AUTH_REQUIRED' : 'HSM_NETWORK_ERROR');
        const message = typeof data?.message === 'string'
            ? data.message
            : (code === 'AUTH_REQUIRED'
                ? 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.'
                : 'Không thể kết nối tới dịch vụ ký HSM.');

        return {
            code,
            message,
            referenceId: typeof data?.referenceId === 'string' ? data.referenceId : undefined,
            retryable: data?.retryable === true || data?.retryable === 'Y'
        };
    }

    static retrySigningNotification(agreementId: string, data: any = {}): Promise<any> {
        return this.axios.post(`${this.entity}/${agreementId}/signing-notifications/retry`, data).then(res => res.data);
    }

    static agreementSequence(templateAgreementId: string): Promise<any> {
        return this.axios.get(`${this.entity}/agreement-sequence?templateAgreementId=${templateAgreementId}`).then(res => res.data);
    }

    static fillPlaceholder(data: any): Promise<any> {
        return this.axios.post(`/iam-main/template-agreements/fill-placeholder`, data, { skipLoading: true } as any).then(res => res.data);
    }

    static cancelAgreement(agreementId: string, data: any): Promise<any> {
        return this.axios.put(`${this.entity}/${agreementId}/cancel`, data).then(res => res.data);
    }

    static convertToDraft(agreementId: string): Promise<any> {
        return this.axios.put(`${this.entity}/${agreementId}/convert-to-draft`).then(res => res.data);
    }

    static getHeaderInfo(code: string, signingSecretCode?: string | null): Promise<any> {
        return this.axios.get(`${this.entity}/header-info`, { params: { code, signingSecretCode: signingSecretCode || undefined } }).then(res => res.data);
    }

    static getAgreementFiles(agreementId: string): Promise<any> {
        return this.axios.get(`${this.entity}/${agreementId}/files`).then(res => res.data);
    }

    static getAgreementInfo(agreementId: string): Promise<any> {
        return this.axios.get(`${this.entity}/${agreementId}/info`).then(res => res.data);
    }

    static updateAgreementFiles(agreementId: string, data: any): Promise<any> {
        return this.axios.put(`${this.entity}/${agreementId}/files`, data).then(res => res.data);
    }

    static unsignedParties(agreementId: string): Promise<any> {
        return this.axios.get(`${this.entity}/${agreementId}/unsigned-parties`).then(res => res.data);
    }

    static uploadSignedFile(agreementId: string, partyId: string, signedPdfBase64: string): Promise<any> {
        const formData = new FormData();

        const byteCharacters = atob(signedPdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        const file = new File([blob], `signed_${agreementId}_${partyId}.pdf`, { type: 'application/pdf' });
        formData.append('contentFile', file);
        formData.append('signatureData', JSON.stringify([{
            partyId: partyId,
            roleTypeId: 'BuyerSignedExec',
            isSigned: 'Y',
            signedDate: Date.now(),
            description: 'Đã ký số thành công'
        }]));

        return this.axios.post(`${this.entity}/${agreementId}/pdf`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then(res => res.data);
    }

    static pullSigningData(data: {
        agreementId: string;
        partyId: string;
        roleTypeId: string;
        referenceNumber?: string;
        signingSecretCode?: string;
        isCheck?: string;
    }): Promise<{
        base64FileData: string;
        signatureLocationList: Array<{ locationSeqId: string; x: number; y: number; width: number; height: number; page: number; fontSize: number; name: string; }>;
        documentList?: Array<{
            agreementContentId: string;
            fileName: string;
            base64FileData: string;
            signatureLocationList: Array<any>;
        }>;
        signingDate: string;
        partyIdValue?: string;
        checkSubjectUid?: string;
        fileName?: string;
        displayConfig?: any;
        errors?: any;
    }> {
        try {
            if (typeof document !== 'undefined' && document.cookie) {
                const cookiePairs = document.cookie.split(';');
                const hostname = window.location.hostname;
                const domainParts = hostname.split('.');
                const domainsToClear: string[] = [];
                domainsToClear.push(hostname);
                if (domainParts.length > 1) {
                    domainsToClear.push('.' + domainParts.slice(-2).join('.'));
                }
                cookiePairs.forEach(pair => {
                    const name = pair.split('=')[0]?.trim();
                    if (!name) return;
                    ['/', ''].forEach(path => {
                        domainsToClear.forEach(domain => {
                            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path || '/'}; domain=${domain}`;
                        });
                    });
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
                });
            }
        } catch (e) {
        }
        return this.axios.put(`${this.entity}/pull-data`, data)
            .then(res => res.data)
            .then((resp: any) => {
                const defaultFontSize = 10;
                
                // Helper to map signature positions
                const mapLocations = (list: any[]) => {
                    return (list || []).map((b: any, idx: number) => {
                        const locationSeqId = String(b?.locationSeqId ?? idx + 1);
                        const pageNumber = Number(b?.pageNumber ?? 1);
                        const llx = Number(b?.llx ?? 0);
                        const lly = Number(b?.lly ?? 0);
                        const widthPx = Number(b?.widthPx ?? 0);
                        const heightPx = Number(b?.heightPx ?? 0);
                        const pageWidth = Number(b?.pageWidth ?? 0);
                        const pageHeight = Number(b?.pageHeight ?? 0);
                        const fontSize = Number(b?.fontSize ?? defaultFontSize);
                        const name = String(b?.name ?? `SignatureId${locationSeqId}`);
                        const roleTypeId = String(b?.roleTypeId ?? '');

                        return {
                            locationSeqId,
                            name,
                            fontSize,
                            pageNumber,
                            widthPx,
                            heightPx,
                            llx,
                            lly,
                            pageWidth,
                            pageHeight,
                            page: pageNumber,
                            x: llx,
                            y: lly,
                            width: widthPx,
                            height: heightPx,
                            roleTypeId,
                            signatureTypeEnumId: b?.signatureTypeEnumId,
                            visualData: b?.visualData,
                        };
                    });
                };

                resp.signatureLocationList = mapLocations(resp.signatureLocationList);
                
                if (Array.isArray(resp.documentList)) {
                    resp.documentList = resp.documentList.map((doc: any) => ({
                        ...doc,
                        signatureLocationList: mapLocations(doc.signatureLocationList)
                    }));
                }

                return resp;
            });
    }
    static pushSignedData(params: {
        signedBy: string;
        base64FileData?: string;
        agreementId?: string;
        partyId?: string;
        roleTypeId: string;
        referenceNumber?: string | null;
        signingSecretCode?: string;
        signedByName?: string;
        signedFiles?: Array<{
            agreementContentId: string;
            base64FileData: string;
        }>;
    }): Promise<any> {
        return this.axios.post(`${this.entity}/push-data`, params).then(res => res.data);
    }

    static updateSigningError(params: {
        agreementId?: string;
        partyId?: string;
        roleTypeId?: string;
        referenceNumber?: string;
        signingSecretCode?: string;
    }): Promise<any> {
        return this.axios.put(`${this.entity}/update-signing-error`, params).then(res => res.data);
    }
    static getDetail(agreementId: string): Promise<any> {
        return this.axios.get(`${this.entity}/${agreementId}/detail`).then(res => res.data);
    }
    static saveFile(fileId: string, fileName: string): Promise<any> {
        return this.axios.put(`${this.entity}/document-name`, { fileId, fileName }).then(res => res.data);
    }
    static openContent(agreementId: string | null | undefined, fileId: string): Promise<any> {
        return this.axios.post(`${this.entity}/open-content`, {
            agreementId,
            fileId
        }).then(res => res.data);
    }
    static syncEditorToMinio(googleFileId: string, fileName: string, contentType: string): Promise<any> {
        return this.axios.post(`${this.entity}/sync-editor-to-minio`, {
            googleFileId,
            fileName,
            contentType
        }).then(res => res.data);
    }
    static closeContent(agreementId: string | null | undefined, fileId: string): Promise<any> {
        return this.axios.post(`${this.entity}/close-content`, {
            agreementId,
            fileId
        }).then(res => res.data);
    }
    static uploadFile(file: File): Promise<any> {
        const formData = new FormData();
        formData.append('contentFile', file);

        return this.axios.post(`${this.entity}/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            skipLoading: true
        } as any).then(res => res.data);
    }
    static generatePdf(fileId: string, fileName?: string, contentType?: string, agreementContentId?: string): Promise<any> {
        return this.axios.post(`${this.entity}/generate-pdf`, { fileId, fileName, contentType, agreementContentId }, { skipLoading: true } as any).then(res => res.data);
    }
    static generateDocumentCode(agreementConfigureEnumId: string): Promise<any> {
        return this.axios.get(`${this.entity}/agreementNo`, { params: { agreementConfigureEnumId } }).then(res => res.data);
    }
    static saveSignaturePositions(agreementId: string, data: any): Promise<any> {
        return this.axios.put(`${this.entity}/${agreementId}`, data).then(res => res.data);
    }
    static getAgreementList(
        lazyLoadEvent: any,
        agreementType?: string | Array<string>,
        agreementConfigureEnumId?: string
    ): Promise<any> {
        return this.axios.get(`${this.entity}/agreements`, {
            params: { lazyLoadEvent, agreementType, agreementConfigureEnumId }
        }).then(res => res.data);
    }
    static deleteAgreementContent(agreementId: string, fileId: string): Promise<any> {
        return this.axios.delete(`${this.entity}/${agreementId}/content`, { params: { fileId } }).then(res => res.data);
    }
    static moveToTrash(agreementId: string): Promise<any> {
        return this.axios.put(`${this.entity}/${agreementId}/trash`).then(res => res.data);
    }
    static restoreAgreement(agreementId: string): Promise<any> {
        return this.axios.put(`${this.entity}/${agreementId}/restore`).then(res => res.data);
    }
    static signAgreement(agreementId: string): Promise<any> {
        return this.axios.put(`${this.entity}/${agreementId}/sign`).then(res => res.data);
    }
    static getAgreementSignings(lazyLoadEvent: any): Promise<any> {
        const params = {
            lazyLoadEvent: typeof lazyLoadEvent === 'string' ? lazyLoadEvent : JSON.stringify(lazyLoadEvent)
        };
        return this.axios.get(`${this.entity}/agreement-signing`, { params }).then(res => res.data);
    }
    
    static bulkAgreementFromTemplate(file: File, templateAgreementId: string): Promise<any> {
        const formData = new FormData();
        formData.append('contentFile', file);
        formData.append('templateAgreementId', templateAgreementId);
        
        return this.axios.post(`/iam-main/template-agreements/${templateAgreementId}/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            timeout: 600000 // Tăng timeout cho tác vụ sinh nhiều hợp đồng
        }).then(res => res.data);
    }

    static downloadExcelTemplate(templateAgreementId: string, customFileName?: string): Promise<any> {
        return this.axios.get(`/iam-main/template-agreements/${templateAgreementId}/download-template`, {
            responseType: 'blob'
        }).then(res => {
            const contentDisposition = this.getHeaderValue(res.headers, 'Content-Disposition');
            let fileName = customFileName ? (customFileName.toLowerCase().endsWith('.xlsx') ? customFileName : `${customFileName}.xlsx`) : `template_${templateAgreementId}.xlsx`;
            
            if (!customFileName && contentDisposition) {
                // Ưu tiên lấy filename* theo chuẩn RFC 5987 (có encoding)
                const fileNameStarMatch = contentDisposition.match(/filename\*=[^']+'[^']*'([^;]+)/);
                if (fileNameStarMatch && fileNameStarMatch[1]) {
                    try {
                        fileName = decodeURIComponent(fileNameStarMatch[1]);
                    } catch (e) {
                        console.error('Lỗi khi giải mã tên file từ filename*:', e);
                    }
                } else {
                    // Nếu không có filename*, lấy filename thông thường
                    const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/);
                    if (fileNameMatch && fileNameMatch[1]) {
                        try {
                            // Một số server có thể gửi filename thường nhưng vẫn URL-encoded
                            fileName = decodeURIComponent(fileNameMatch[1]);
                        } catch (e) {
                            fileName = fileNameMatch[1];
                        }
                    }
                }
            }

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        });
    }
    static downloadPdf(fileId: any): Promise<Blob> {
        console.log('downloadPdf called with fileId:', fileId);
        return this.axios.post(`${this.entity}/download-pdf`, { fileId }, { responseType: 'arraybuffer' })
            .then(res => {
                console.log('downloadPdf response:', res.status, res.headers);
                const contentType = this.getHeaderValue(res.headers, 'Content-Type') || 'application/pdf';
                return new Blob([res.data], { type: contentType });
            }).catch(error => {
                console.error('downloadPdf error:', error);
                throw error;
            });
    }

    static downloadFile(fileId: any, options?: { original?: boolean, contentLocation?: string }): Promise<Blob & { fileName?: string }> {
        return this.axios.post(`${this.entity}/download-pdf`, { 
            fileId, 
            contentLocation: options?.contentLocation,
            original: options?.original 
        }, { responseType: 'arraybuffer' })
            .then(res => {
                const contentType = this.getHeaderValue(res.headers, 'Content-Type') || 'application/octet-stream';
                
                let fileName: string | null = null;
                const contentDisposition = this.getHeaderValue(res.headers, 'Content-Disposition');
                if (contentDisposition) {
                    const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;\n]*)/);
                    if (filenameStarMatch && filenameStarMatch[1]) {
                        try { fileName = decodeURIComponent(filenameStarMatch[1]); } catch (e) {}
                    }
                    if (!fileName) {
                        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                        if (filenameMatch && filenameMatch[1]) {
                            fileName = filenameMatch[1].replace(/['"]/g, '');
                            try { fileName = decodeURIComponent(fileName); } catch (e) {}
                        }
                    }
                }
                
                const blob = new Blob([res.data], { type: contentType }) as Blob & { fileName?: string };
                if (fileName) blob.fileName = fileName;
                return blob;
            });
    }

    static openEditor(agreementId: string | null | undefined, fileId: string): Promise<any> {
        console.log('>>> AgreementService.openEditor called:', { agreementId, fileId });
        return this.axios.post(`${this.entity}/open-editor`, { agreementId, fileId }).then(res => {
            console.log('>>> openEditor response:', res.data);
            return res.data;
        }).catch(error => {
            console.error('>>> openEditor error:', error);
            throw error;
        });
    }

    static saveEditor(agreementId: string | null | undefined, fileId: string, googleFileId: string): Promise<any> {
        console.log('>>> AgreementService.saveEditor called:', { agreementId, fileId, googleFileId });
        return this.axios.post(`${this.entity}/save-editor`, { agreementId, fileId, googleFileId }).then(res => {
            console.log('>>> saveEditor response:', res.data);
            return res.data;
        }).catch(error => {
            console.error('>>> saveEditor error:', error);
            throw error;
        });
    }
    static pullBatchData(agreementIds: any[]): Promise<any> {
        return this.axios.post(`${this.entity}/pull-batch-data`, { agreementIds }).then(res => res.data);
    }
    static pushBatchData(signedAgreements: any[]): Promise<any> {
        return this.axios.post(`${this.entity}/push-batch-data`, { signedAgreements }).then(res => res.data);
    }
    static updateBatchSigningErr(agreementIds: any[]): Promise<any> {
        return this.axios.post(`${this.entity}/update-batch-signing-err`, { agreementIds }).then(res => res.data);
    }
}
